import React, { useState, useMemo, useRef, useEffect } from "react";
import { RCCAlert, RCCButton, RCCGrid, RCCHookToast, RCCIcon, RCCServicesApi, RCCTooltip } from "react-common-components";
import { useForm } from "react-hook-form";
import PageTitle from "src/components/PageTitle";
import Select from "src/components/Select";
import { useTranslation } from "src/context/Translations/context";
import ConfigsContainer from "src/components/ConfigsContainer";
import formatDate from "src/utils/formatDate";
import { standardErrorMessages } from "src/utils/errors";
import Loader from "src/components/Loader";
import ButtonScrollTo from "src/components/ButtonScrollTo";
import LovSubsidiary from "src/components/Lov/Subsidiary";
import DateTimePicker from "src/components/DateTimePicker";
import ModalIndicatorChart from "src/pages/Indicators/Panel/components/ModalIndicatorChart";
import { IRefActionsModalChartIndicators } from "src/pages/Indicators/Panel/components/ModalIndicatorChart/interface";
import { IRefExportListComponent } from "src/components/List/interface";
import downloadFile from "src/utils/downloadFile";
import ExportListContainer from "src/components/List/exportList";
import { useHistory } from "react-router-dom";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import LovCoins from "src/components/Lov/Coins";
import Title from "src/components/Title";
import { IComponentConfigsPerformance, IConfigsPerformance, IDataTable, IValuesTable } from "./interface";
import LovIncomeStatementWithOptions from "../IncomeStatement/components/LovIncomeStatementWithOptions";
import TableOfPerformance from "./tableOfPerformance";

const Performance: React.FC<IComponentConfigsPerformance> = ({
	budgetPlanId,
	budgetPlanTitle,
	configs: ParamsConfigs,
	subsidiaryDefaultValue
}) => {
	const { confirm } = useConfirmModal();
	const history = useHistory();
	const { translate } = useTranslation();
	const { addToast } = RCCHookToast();
	const [subsidiary, setSubsidiary] = useState<{
		title: string;
		id: number;
	} | undefined>(undefined);
	
	const baseVerticalValues = useRef<IValuesTable[]>();
	const modalChartIndicatorsValues = useRef<IRefActionsModalChartIndicators>(null);
	const [showConfigs, setShowConfigs] = useState(false);
	const [configs, setConfigs] = useState<IConfigsPerformance>(ParamsConfigs);
	const { control: controlConfigs } = useForm();


	const { handleSubmit : handleSubmitFormOptions, control: controlFormOptions, setValue: setValueFormOptions } = useForm();
	const submitFormOptions = async (data: any) => {
		const dataForm = data;	
		loadData({ ...configs, ...dataForm});
	};

	const [dataTable, setDataTable] = useState<IDataTable[]>([]);
	const [loading, setLoading] = useState(false);
	const [errorLoadingData, setErrorLoadingData] = useState(false);
	const [hasConversionValueEqualZero, setHasConversionValueEqualZero] = useState(false);
	const loadData = async (dataForm: any) => {
		if(!budgetPlanId)
			return;
			
		try{
			setLoading(true);
			setErrorLoadingData(false);

			const urlParams = {
				ofa001Id: budgetPlanId,
				ofm001Id: dataForm.ofm001Id,
				subsidiary: dataForm.subsidiary || "",
				startDate: dataForm.startDate ? formatDate(dataForm.startDate) : "",
				divider: dataForm.divider,
				coinPlanned: coinConversion && dataForm.coinPlanned && dataForm.coinPlanned !== null ? dataForm.coinPlanned.toString() : "",
				coinRealized: coinConversion && dataForm.coinRealized && dataForm.coinRealized !== null ? dataForm.coinRealized.toString() : ""
			};

			const {data} = await RCCServicesApi.getRequest({
				method: "FinancialManagement/Performance",
				urlParameters: {
					...urlParams
				}
			});

			setConfigs((prevState) => ({
				...prevState,
				startDate: dataForm.startDate || new Date(new Date().getFullYear(), new Date().getMonth(), 1)
			}));
			setHasConversionValueEqualZero(data.hasConversionValueEqualZero);
			setDataTable(data.list);
			baseVerticalValues.current = data.baseVerticalAnalysis;
			setLoading(false);
		}catch(err){
			setErrorLoadingData(true);
			standardErrorMessages(err, translate("Erro ao carregar os dados"), addToast);
			setLoading(false);
		}
	};

	const [loadingExport, setLoadingExport] = useState(false);
	const exportListContainerRef = useRef<IRefExportListComponent>(null);
	const exportStructure = async () => {
		const data = exportListContainerRef.current?.executeExport();

		if(!data || data.length === 0){
			addToast({
				title: translate("Erro"),
				description: translate("Não há valores para exportar"),
				color: "yellow"
			});
			return;	
		}

		try{	
			setLoadingExport(true);

			downloadFile(data.join("\n"), `Performance - ${budgetPlanTitle}.csv`, true);

			addToast({
				color: "green",
				description: translate("Estrutura exportada com sucesso"),
				title: translate("Sucesso")
			});

			setLoadingExport(false);
		}catch(err){
			setLoadingExport(false);
			standardErrorMessages(err, translate("Erro ao exportar estrutura valores"), addToast);
		}
	};

	const [corporativeValueOutdated, setCorporativeValueOutdated] = useState(false);
	const [coinConversion, setCoinConversion] = useState(false);
	const fetchIfBudgetUsesApportionment = async () => {
		if(!budgetPlanId)
			return;
		try{
			const {data} = await RCCServicesApi.getRequest({
				method: "FinancialManagement/GetConfig",
				urlParameters: {
					budgetId: budgetPlanId,
					origin: "5"
				}
			});
			setCorporativeValueOutdated(data.budget.off001Usarateiocc === "S" && data.budgetPlan?.ofa001Vlrcorpatu === "N");
			setCoinConversion(data.budgetPlan?.ofa001Utilizamoeda === "S");
		}catch(err){
			setCorporativeValueOutdated(false);
		}
	};
	const updateCorporativeValues = () => {
		confirm(
			translate("Atualizar valores corporativos"), 
			`${translate("Esse processo pode demorar alguns minutos, deseja continuar")}?`,
			async () => {
				if(!budgetPlanId)
					return;

				await RCCServicesApi.putRequest({
					method: "FinancialManagement/UpdateCorporativeValues",
					urlParameters: {
						budgetPlanId
					}
				});

				setCorporativeValueOutdated(false);

				addToast({
					color: "green",
					description: translate("Valores corporativos atualizados com sucesso"),
					title: translate("Sucesso")
				});
			},
			(err) => {
				standardErrorMessages(err, translate("Erro ao atualizar valores corporativos"), addToast);
			},
			false
		);
	};

	const Table = useMemo(() => {
		if(loading)
			return <Loader />;
		if(errorLoadingData)
			return  <RCCAlert 
				title={translate("Atenção")}
				description={translate("Erro ao tentar encontrar resultados para esta pesquisa")}
			/>;
		
		return <TableOfPerformance 
			data={dataTable as IDataTable[]}
			subsidiary={subsidiary?.title}
			openChartIndicators={(id: number, title: string | null) => {
				modalChartIndicatorsValues.current?.actionButtonOpenChartIndicators(id, title || "", new Date(configs.startDate).getFullYear().toString(), true, null, null);
			}}
			coinPlannedInitials={configs.coinPlannedInitials !== null ? configs.coinPlannedInitials : undefined}
			coinRealizedInitials={configs.coinRealizedInitials !== null ? configs.coinRealizedInitials : undefined}
		/>;

	}, [loading, errorLoadingData, dataTable]);

	
	useEffect(() => {
		fetchIfBudgetUsesApportionment();
	}, []);

	return (
		<>

			{/* Header -> Results + Buttons */}
			<PageTitle
				title={`Performance - ${budgetPlanTitle}`}
				icon={{
					name: "dollar"
				}}
			>
				{corporativeValueOutdated && (
					<RCCTooltip
						text={translate("Valores corporativos desatualizados")}
						color="yellow"
					>
						<RCCButton
							icon={{name: "exclamationTriangle", color: "#FFF"}}
							color="yellow"
							layout="isRounded"
							size="md"
							onClick={updateCorporativeValues}
						/>
					</RCCTooltip>
				)}
				<RCCTooltip
					text={translate("Configurações")}
					color="blue"
				>
					<RCCButton
						icon={{name: "settings", color: "#FFF", size: 16}}
						color="blue"
						layout="isRounded"
						size="md"
						onClick={() => setShowConfigs((prevState) => !prevState)}
					/>
				</RCCTooltip>
				<RCCTooltip
					text={translate("Voltar")}
					color="grey"
				>
					<RCCButton
						icon={{name: "arrowBack", color: "#FFF"}}
						color="grey"
						layout="isRounded"
						size="md"
						onClick={() => history.push(`/financialmanagement/panel/${budgetPlanId}`)}
					/>
				</RCCTooltip>
			</PageTitle>

			{/* Configs */}
			<ConfigsContainer
				showConfigs={showConfigs}
				showClearConfigs={false}
				showSubmitConfigs={false}
			>
				<RCCGrid
					container
					item
					spacing={1}
				>
					<RCCGrid
						container 
						item
						xs={12}
						sm={6}
						md={3}
						lg={3}
					>
						<Select 
							name="divider"
							control={controlConfigs}
							defaultValue={configs.divider}
							options={[{
								label: "1",
								value: "1"
							},{
								label: "1000",
								value: "2"
							},{
								label: "1000000",
								value: "3"
							}]}
							label={translate("Divisor dos valores")}
							onChangeCapture={(e) => setConfigs((prevState) => ({ ...prevState, divider: e.value }))}
						/>
					</RCCGrid>
					{coinConversion && (
						<>
							<RCCGrid
								container 
								item
								xs={12}
								sm={6}
								md={3}
								lg={3}
							>
								<LovCoins 
									name="coinPlanned"
									label={translate("Moeda planejado")}
									control={controlConfigs}
									defaultValue={configs.coinPlanned !== null ? configs.coinPlanned.toString() : undefined}
									isClearable
									onChangeCapture={(e: any) => setConfigs((prevState) => ({ 
										...prevState, 
										coinPlanned: e != null ? e.value : null,
										coinPlannedInitials: e != null ? e.extendedProps.moa001Sigla : null  
									}))}
								/>
							</RCCGrid>
							<RCCGrid
								container 
								item
								xs={12}
								sm={6}
								md={3}
								lg={3}
							>
								<LovCoins 
									name="coinRealized"
									label={translate("Moeda realizado")}
									control={controlConfigs}
									defaultValue={configs.coinRealized !== null ? configs.coinRealized.toString() : undefined}
									isClearable
									onChangeCapture={(e: any) => setConfigs((prevState) => ({ 
										...prevState, 
										coinRealized: e != null ? e.value : null,
										coinRealizedInitials: e != null ? e.extendedProps.moa001Sigla : null 
									}))}
								/>
							</RCCGrid>
						</>
					)}
				</RCCGrid>	 
			</ConfigsContainer>
			
			{/* Groupers Select -> Origin -> Delayed */}
			<form onSubmit={handleSubmitFormOptions(submitFormOptions)} style={{marginBottom: "1rem"}}>
				<RCCGrid
					container 
					item
					spacing={1}
				>
					<RCCGrid
						container
						item
						xs={12}
						sm={6}
						md={4}
						lg={4}
					>
						<LovIncomeStatementWithOptions 
							control={controlFormOptions}
							budgetPlanId={Number(budgetPlanId)}
							setValue={setValueFormOptions}
							type={2}
						/>
					</RCCGrid>
				</RCCGrid>
				
				<RCCGrid
					container
					item
					spacing={1}
					alignItems="center"
				>

					<RCCGrid
						container
						item
						xs={12}
						sm={6}
						md={4}
						lg={4}
					>
						<LovSubsidiary 
							control={controlFormOptions}
							name="subsidiary"
							label={translate("Unidade/Filial")}
							defaultValue={subsidiaryDefaultValue}
							extraParametersOnLoad={{
								budgetPlanId: Number(budgetPlanId)
							}}
							onChangeCapture={(e: any) => {setSubsidiary({
								title: e.label,
								id: Number(e.value)
							});
							}}
							setValue={setValueFormOptions}
						/>
					</RCCGrid>

					<RCCGrid
						container 
						item
						xs={12}
						sm={6}
						md={4}
						lg={4}
					>
						<DateTimePicker
							control={controlFormOptions}
							label={translate("Período")}
							name="startDate"
							defaultValue={configs.startDate}
							type="datePicker"
							props={{
								datePicker: {
									format: "MM/yyyy",
									views: ["year", "month"]
								}
							}}
						/>
					</RCCGrid>
				</RCCGrid>

				<RCCGrid container item spacing={1} justifyContent="flex-end">
					{hasConversionValueEqualZero && (
						<RCCGrid container item alignItems="center" style={{ flex: 1 }}>
							<RCCIcon name="exclamationTriangle" color="var(--yellow)" />
							<Title color="defaultColor" size="xs">
								{translate("Existem períodos em que não foi cadastrado a conversão da moeda")}
							</Title>
						</RCCGrid>
					)}
					{dataTable && dataTable.length > 0 && (
						<RCCTooltip
							text={translate("Exportar")}
							color="purple"
						>
							<RCCButton
								color="purple"
								icon={{
									name: "download"
								}}
								layout="isRounded"
								size="md"
								loadingText=""
								loading={loadingExport}
								onClick={exportStructure}
							/>
						</RCCTooltip>
					)}
					<RCCTooltip
						text={translate("Consultar")}
						color="purple"
					>
						<RCCButton
							color="purple"
							icon={{
								name: "search"
							}}
							layout="isRounded"
							size="md"
							type="submit"
							loadingText=""
							loading={loading}
						/>
					</RCCTooltip>
				</RCCGrid>
			</form>				
			
			<ExportListContainer
				ref={exportListContainerRef}
				classToBreakLineInsideSameColumn="break-columns-in-csv-here"
			>
				{Table}
			</ExportListContainer>

			<ButtonScrollTo
				minHeightToShowButton={400}
			/>

			<ModalIndicatorChart ref={modalChartIndicatorsValues} 
				openValuesOfPeriodIndicator={() => {}}
				onCloseCallback={() => {}}
			/>

		</>
	);
};

export default Performance;
